import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import { getCardSignals } from "../store/collection-store";
import { calculateShippingDate } from "../utils/knocking";

/**
 * Ships-by date display for collection product cards.
 * Replaces the legacy ships-by-notification component with a reactive Lit implementation.
 *
 * Displays the calculated shipping date in short format (e.g., "Mar 11").
 *
 * Usage: <card-ships-by data-card-id="p-12345"></card-ships-by>
 */
@customElement("card-ships-by")
export class CardShipsBy extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";

  @state() private _shippingDate: string | null = null;
  @state() private _showSoldOut = false;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    // Subscribe to card signals and calculate shipping date
    effect(() => {
      if (!this.cardId) {
        console.warn('card-ships-by: no cardId');
        return;
      }

      const signals = getCardSignals(this.cardId);

      if (!signals) {
        console.warn(`card-ships-by [${this.cardId}]: no signals found`);
        this._shippingDate = null;
        this._showSoldOut = false;
        return;
      }

      const buttonState = signals.buttonState.value;
      const isNotifyState =
        buttonState === "extendedOffer-modal" ||
        buttonState === "extendedOffer-redirect" ||
        buttonState === "cbsNotify";

      if (isNotifyState) {
        this._showSoldOut = true;
        this._shippingDate = null;
        return;
      }

      this._showSoldOut = false;

      if (!signals.shipsBy) {
        console.log(`card-ships-by [${this.cardId}]: no shipsBy value`);
        this._shippingDate = null;
        return;
      }

      try {
        const calculated = calculateShippingDate(signals.shipsBy);
        this._shippingDate = calculated.shortDate || calculated.longDate;
      } catch (error) {
        console.error(`card-ships-by [${this.cardId}]: failed to calculate date`, error);
        this._shippingDate = null;
      }
    });
  }

  render() {
    if (this._showSoldOut) {
      return html`
        <div
          id="product-shipsby-notice"
          class="product__shipsby-notice h-full bg-[var(--color-secondary-50)] uppercase text-xs text-[var(--color-primary-950)] tracking-[0.1em] py-[5.5px] px-4 text-center flex flex-col justify-center items-center"
        >
          <p class="flex flex-col text-center">
            <span class="text-sm font-bold text-[var(--color-secondary-950)]">Sold Out</span>
          </p>
        </div>
      `;
    }

    if (!this._shippingDate) {
      return html`<!-- card-ships-by: no shipping date -->`;
    }

    return html`
      <div
        id="product-shipsby-notice"
          class="product__shipsby-notice bg-[var(--color-primary-100)] h-full uppercase text-xs text-[var(--color-primary-950)] tracking-[0.1em] py-[5.5px] px-4 text-center flex flex-col justify-center items-center"
      >
        <p class="flex flex-col text-center">
          <span class="text-xs text-[var(--color-primary-950)] uppercase">Ships by</span>
          <span class="text-sm font-bold text-[var(--color-primary-950)]">${this._shippingDate}</span>
        </p>
      </div>
    `;
  }
}
